import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Box, useMediaQuery, Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useLocation, useParams } from 'react-router-dom';
import TicketOrderSearch from './newTicket/SpecialFieldTypes/TicketOrderSearch';
import { editTicketData } from '../../../../redux/slices/tickets';
import { useDispatch, useSelector } from '../../../../redux/store';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';

AttachOrderToTicket.propTypes = {
  setIsEditingOrder: PropTypes.func,
};

export default function AttachOrderToTicket({ setIsEditingOrder }) {
  const { id = '' } = useParams();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { mainTicket } = useSelector((state) => state.tickets.data.ticketInfoById[id]) || {};
  const subticketId = new URLSearchParams(location.search).get('subticket');
  const { ticketNumber } = useSelector((state) => state.tickets.data.ticketById[subticketId || id]) || {};
  const [selectedOrderId, setSelectedOrderId] = useState(mainTicket?.ticketOrderId || mainTicket?.ticketRentalId || null);
  const [showOrderPicker, setShowOrderPicker] = useState(null);
  const [orderType, setOrderType] = useState(mainTicket?.ticketOrderId ? 'Purchase' : mainTicket?.ticketRentalId ? 'Rental' : null);

  const hasChanges = mainTicket && selectedOrderId !== mainTicket?.ticketOrderId && selectedOrderId !== mainTicket?.ticketRentalId;

  const editTicket = async ({ Id, value, updateType }) => {
    const response = await dispatch(editTicketData({ Id: ticketNumber || Id, value, updateType, mainId: id }));
    if (response) {
      enqueueSnackbar(`Ticket updated successfully`, { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to update ticket', { variant: 'error' });
    }
  };

  const updateTicketOrder = async (ticketOrderId) => {
    try {
      await editTicket({
        Id: ticketNumber || id,
        value: ticketOrderId ?? 0,
        updateType: orderType === 'Rental' ? 'TicketRentalId' : 'TicketOrderId',
      });
    } catch (error) {
      enqueueSnackbar('Failed to update ticket', { variant: 'error' }); 
    }
    if (setIsEditingOrder) setIsEditingOrder(false);
  };

  useEffect(() => {
    if (!mainTicket) return;
    setSelectedOrderId(mainTicket?.ticketOrderId || mainTicket?.ticketRentalId);
    setShowOrderPicker((mainTicket.ticketOrderId > 0 || mainTicket.ticketRentalId > 0));
    setOrderType(mainTicket?.ticketOrderId ? 'Purchase' : mainTicket?.ticketRentalId ? 'Rental' : null);
  }, [mainTicket?.ticketOrderId, mainTicket?.ticketRentalId]);

  return (
    <Box sx={{ width: '100%', maxWidth: 300 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mt: isMobile ? '4px' : 0 }}>
        {showOrderPicker !== null && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!showOrderPicker && (
              <Button
                variant="text"
                onClick={() => setShowOrderPicker(true)}
                sx={{
                  p: 0,
                  minWidth: 'auto',
                  textDecoration: 'underline',
                  justifyContent: 'flex-start',
                  fontWeight: 500,
                  color: 'info.main',

                  '&:hover': (theme) => ({
                    backgroundColor: theme.palette.action.hover,
                    textDecoration: 'underline',
                  }),
                }}
              >
                Attach order
              </Button>
            )}
            {showOrderPicker && (
              <TicketOrderSearch
                facilityId={mainTicket?.facilityId}
                onChange={(order) => {
                  if (selectedOrderId !== order?.id) {
                    if(order) setOrderType(order?.orderType);
                    setSelectedOrderId(order?.id);
                  }
                }}
                sx={{ width: '100%' }}
                inputVariant="standard"
                selectedOrderId={selectedOrderId}
              />
            )}
          </Box>
        )}
        <Box sx={{ flexShrink: 0, ml: 1, mb: isMobile ? '16px' : 0 }}>
          {hasChanges && (
            <AwaitButton
              sx={{ minWidth: 0, padding: isMobile ? '0px' : '5px 15px' }}
              onClick={async () => updateTicketOrder(selectedOrderId)}
              variant={isMobile ? 'text' : 'outlined'}
            >
              {isMobile ? <CheckIcon /> : 'Save'}
            </AwaitButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}
